import React, { useState } from 'react';
import { Button, Select, DatePicker, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { GET_OATT_CUSTOMERS } from 'graphql/oattcustomers.gql';
import styles from './OattAuditFilterPanel.module.css';

const { RangePicker } = DatePicker;

export type OattDatePreset = 'today' | 'last7' | 'thisMonth' | 'custom';

export interface OattAuditFilters {
  startDate: string;
  endDate: string;
  customerId?: number | null;
  suppressQuery?: boolean;
}

interface OattCustomer {
  customer_id: number;
  customer_code: string;
  company_name: string | null;
}

interface Props {
  recordCount: number;
  onChange: (filters: OattAuditFilters) => void;
}

const PRESET_LABELS: { key: OattDatePreset; label: string }[] = [
  { key: 'today',     label: 'Today' },
  { key: 'last7',     label: 'Last 7 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'custom',    label: 'Custom Range' },
];

export function getOattDateRange(
  preset: OattDatePreset,
  custom: [Dayjs, Dayjs] | null = null,
): { startDate: string; endDate: string } {
  const now = dayjs();
  if (preset === 'today') return {
    startDate: now.startOf('day').toISOString(),
    endDate: now.add(1, 'day').startOf('day').toISOString(),
  };
  if (preset === 'last7') return {
    startDate: now.subtract(6, 'day').startOf('day').toISOString(),
    endDate: now.add(1, 'day').startOf('day').toISOString(),
  };
  if (preset === 'thisMonth') return {
    startDate: now.startOf('month').toISOString(),
    endDate: now.add(1, 'month').startOf('month').toISOString(),
  };
  if (preset === 'custom' && custom) return {
    startDate: custom[0].startOf('day').toISOString(),
    endDate: custom[1].add(1, 'day').startOf('day').toISOString(),
  };
  return { startDate: '', endDate: '' };
}

function presetTooltip(key: OattDatePreset): string {
  if (key === 'custom') return 'Shows changes recorded within your selected date range, regardless of invoice date.';
  const { startDate, endDate } = getOattDateRange(key);
  const fmt = (iso: string) => dayjs(iso).format('MMM D, YYYY');
  const end = dayjs(endDate).subtract(1, 'day');
  const range = startDate === endDate || dayjs(startDate).isSame(end, 'day')
    ? fmt(startDate)
    : `${fmt(startDate)} – ${end.format('MMM D, YYYY')}`;
  const prefix: Record<Exclude<OattDatePreset, 'custom'>, string> = {
    today:     'Shows changes recorded today',
    last7:     'Shows changes recorded in the last 7 days',
    thisMonth: 'Shows changes recorded this month',
  };
  return `${prefix[key as Exclude<OattDatePreset, 'custom'>]} (${range}).`;
}

const OattAuditFilterPanel = ({ recordCount, onChange }: Props) => {
  const [preset, setPreset] = useState<OattDatePreset>('thisMonth');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [committedCustomerId, setCommittedCustomerId] = useState<number | null>(null);

  const { data: customersData, loading: customersLoading } = useQuery(GET_OATT_CUSTOMERS, {
    fetchPolicy: 'cache-first',
  });

  const customers: OattCustomer[] = customersData?.oattCustomers ?? [];

  const emit = (
    nextPreset: OattDatePreset,
    nextCustom: [Dayjs, Dayjs] | null,
    nextCustomerId: number | null,
    suppress = false,
  ) => {
    const { startDate, endDate } = getOattDateRange(nextPreset, nextCustom);
    onChange({ startDate, endDate, customerId: nextCustomerId, suppressQuery: suppress });
  };

  const handlePreset = (key: OattDatePreset) => {
    setPreset(key);
    if (key === 'custom') {
      setShowRangePicker(true);
      emit(key, customRange, committedCustomerId);
    } else {
      setShowRangePicker(false);
      setCustomRange(null);
      emit(key, null, committedCustomerId);
    }
  };

  const handleCustomRange = (vals: [Dayjs | null, Dayjs | null] | null) => {
    const range = vals && vals[0] && vals[1] ? ([vals[0], vals[1]] as [Dayjs, Dayjs]) : null;
    setCustomRange(range);
    emit('custom', range, committedCustomerId);
  };

  const handleCustomerChange = (value: number | undefined) => {
    const id = value ?? null;
    setSelectedCustomerId(id);
    setCommittedCustomerId(null);
    emit(preset, customRange, null, !!id);
  };

  const handleSearch = () => {
    setCommittedCustomerId(selectedCustomerId);
    emit(preset, customRange, selectedCustomerId);
  };

  return (
    <div className={styles.filterPanel} data-testid="audit-filter-panel" data-preset={preset}>
      <div className={styles.filterRow}>
        <div className={styles.customerGroup}>
          <span className={styles.label}>Search by Customer:</span>
          <Select
            size="small"
            allowClear
            showSearch
            placeholder="Select customer code"
            loading={customersLoading}
            value={selectedCustomerId ?? undefined}
            onChange={handleCustomerChange}
            optionFilterProp="label"
            className={styles.customerSelect}
            data-testid="audit-customer-select"
            options={customers.map((c) => ({
              value: c.customer_id,
              label: c.company_name ? `${c.customer_code} – ${c.company_name}` : c.customer_code,
            }))}
          />
          <Button
            size="small"
            type="primary"
            icon={<SearchOutlined />}
            disabled={!selectedCustomerId}
            onClick={handleSearch}
            data-testid="customer-search-button"
          >
            Search
          </Button>
        </div>

        <div className={styles.presetGroup}>
          {PRESET_LABELS.map(({ key, label }) => (
            <Tooltip key={key} title={presetTooltip(key)}>
              <Button
                type="text"
                size="small"
                onClick={() => handlePreset(key)}
                className={`${styles.presetBtn}${preset === key ? ` ${styles.presetBtnActive}` : ''}`}
                data-testid="date-preset-button"
                data-preset={key}
                data-active={String(preset === key)}
              >
                {label}
              </Button>
            </Tooltip>
          ))}
          {showRangePicker && (
            <span data-testid="custom-range-picker">
            <RangePicker
              size="small"
              value={customRange}
              onChange={handleCustomRange}
              disabledDate={(d) => d.isAfter(dayjs())}
            />
            </span>
          )}
        </div>

        <span className={styles.recordCount} data-testid="record-count" data-count={String(recordCount)}>
          {recordCount} record{recordCount !== 1 ? 's' : ''} displayed
        </span>
      </div>
    </div>
  );
};

export default OattAuditFilterPanel;
